import { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  Sparkles,
  BookOpen,
  Package,
  Layers,
  MoreHorizontal,
  Edit2,
  Copy,
  Trash2,
  CheckCircle,
  ChefHat,
  Timer,
  Users,
  DollarSign,
  Loader2,
  LayoutGrid,
  List,
  Eye,
  MapPin,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import {
  useStandardizedTemplates,
  useTemplateStats,
  useDeleteTemplate,
  useDuplicateTemplate,
  useApproveTemplate,
  type StandardizedTemplate,
} from '@/hooks/useStandardizedTemplates';
import { toast } from 'sonner';
import { useLocationStore } from '@/store/location';
import { useLocations } from '@/hooks/useLocations';

type ViewMode = 'list' | 'card';

const typeIcons = {
  recipe: ChefHat,
  product: Package,
  bundle: Layers,
};

const statusColors = {
  draft: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  approved: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  archived: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
};

export default function TemplateLibrary() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  
  // View mode with localStorage persistence (default to list)
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem('standardization-view-mode');
    return (saved as ViewMode) || 'list';
  });
  
  // Persist view mode preference
  useEffect(() => {
    localStorage.setItem('standardization-view-mode', viewMode);
  }, [viewMode]);
  
  // Location context for empty state messaging
  const { currentLocation } = useLocationStore();
  const { locations } = useLocations();

  const { data: templates, isLoading } = useStandardizedTemplates();
  const { data: stats } = useTemplateStats();
  const deleteTemplate = useDeleteTemplate();
  const duplicateTemplate = useDuplicateTemplate();
  const approveTemplate = useApproveTemplate();
  
  // Check if viewing a specific location vs all locations
  const isViewingSpecificLocation = !!currentLocation?.id;
  const currentLocationName = currentLocation?.name || 'All Locations';

  // Handle approve
  const handleApprove = async (template: StandardizedTemplate) => {
    try {
      await approveTemplate.mutateAsync(template.id);
      toast.success('Template approved', {
        description: `"${template.name}" has been approved.`,
      });
    } catch (error: any) {
      toast.error('Failed to approve', {
        description: error.message,
      });
    }
  };

  // Filter templates
  const filteredTemplates = useMemo(() => {
    if (!templates) return [];

    return templates.filter((template) => {
      const matchesSearch =
        searchQuery === '' ||
        template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        template.description?.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesType = typeFilter === 'all' || template.type === typeFilter;
      const matchesStatus = statusFilter === 'all' || template.status === statusFilter;
      const matchesCategory =
        categoryFilter === 'all' || template.category === categoryFilter;

      return matchesSearch && matchesType && matchesStatus && matchesCategory;
    });
  }, [templates, searchQuery, typeFilter, statusFilter, categoryFilter]);

  const handleDuplicate = async (template: StandardizedTemplate) => {
    try {
      const newId = await duplicateTemplate.mutateAsync({
        templateId: template.id,
      });
      navigate(`/standardization/${newId}`);
    } catch (error) {
      console.error('Failed to duplicate template:', error);
    }
  };

  const handleDelete = async (template: StandardizedTemplate) => {
    if (!confirm(`Are you sure you want to delete "${template.name}"?`)) return;

    try {
      await deleteTemplate.mutateAsync(template.id);
    } catch (error) {
      console.error('Failed to delete template:', error);
    }
  };

  const statCards = [
    {
      name: 'Total Templates',
      value: stats?.total || 0,
      icon: BookOpen,
      color: 'text-blue-600 bg-blue-100 dark:bg-blue-900/30',
    },
    {
      name: 'Recipes',
      value: stats?.byType.recipe || 0,
      icon: ChefHat,
      color: 'text-orange-600 bg-orange-100 dark:bg-orange-900/30',
    },
    {
      name: 'Products',
      value: stats?.byType.product || 0,
      icon: Package,
      color: 'text-purple-600 bg-purple-100 dark:bg-purple-900/30',
    },
    {
      name: 'Approved',
      value: stats?.byStatus.approved || 0,
      icon: CheckCircle,
      color: 'text-emerald-600 bg-emerald-100 dark:bg-emerald-900/30',
    },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Actions */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Link to="/standardization/discover">
            <Button variant="outline" className="gap-2">
              <Sparkles className="h-4 w-4 text-purple-500" />
              AI Discovery
            </Button>
          </Link>
        </div>
        <Link to="/standardization/new">
          <Button className="gap-2">
            <Plus className="h-4 w-4" />
            New Template
          </Button>
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat) => (
          <div
            key={stat.name}
            className="bg-card rounded-xl border p-4"
          >
            <div className="flex items-center gap-3">
              <div className={cn('p-2 rounded-lg', stat.color)}>
                <stat.icon className="h-5 w-5" />
              </div>
              <div>
                <p className="text-2xl font-semibold">
                  {stat.value}
                </p>
                <p className="text-sm text-muted-foreground">{stat.name}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search templates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Type Filter */}
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="recipe">Recipes</SelectItem>
            <SelectItem value="product">Products</SelectItem>
            <SelectItem value="bundle">Bundles</SelectItem>
          </SelectContent>
        </Select>

        {/* Status Filter */}
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>

        {/* Category Filter */}
        {stats?.categories && stats.categories.length > 0 && (
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {stats.categories.map((cat) => (
                <SelectItem key={cat} value={cat}>
                  {cat}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        
        {/* View Toggle */}
        <div className="flex items-center border rounded-lg p-1 bg-muted/30">
          <Button
            variant={viewMode === 'list' ? 'secondary' : 'ghost'}
            size="sm"
            className="h-8 px-3"
            onClick={() => setViewMode('list')}
          >
            <List className="h-4 w-4" />
          </Button>
          <Button
            variant={viewMode === 'card' ? 'secondary' : 'ghost'}
            size="sm"
            className="h-8 px-3"
            onClick={() => setViewMode('card')}
          >
            <LayoutGrid className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Templates Display */}
      {filteredTemplates.length === 0 ? (
        <div className="bg-card rounded-xl border p-12 text-center">
          <BookOpen className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">
            {isViewingSpecificLocation 
              ? `No templates for ${currentLocationName}`
              : 'No templates found'
            }
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            {searchQuery || typeFilter !== 'all' || statusFilter !== 'all'
              ? 'Try adjusting your filters'
              : isViewingSpecificLocation
                ? `This location has no templates yet. Templates created for "${currentLocationName}" or shared across all locations will appear here.`
                : 'Get started by creating your first template or using AI Discovery'}
          </p>
          {isViewingSpecificLocation && (
            <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground mb-4">
              <MapPin className="h-3.5 w-3.5" />
              <span>Viewing: {currentLocationName}</span>
              {locations.length > 1 && (
                <span className="text-muted-foreground/60">
                  ({locations.length} locations total)
                </span>
              )}
            </div>
          )}
          <div className="flex items-center justify-center gap-3">
            <Link to="/standardization/discover">
              <Button variant="outline" className="gap-2">
                <Sparkles className="h-4 w-4 text-purple-500" />
                Try AI Discovery
              </Button>
            </Link>
            <Link to="/standardization/new">
              <Button className="gap-2">
                <Plus className="h-4 w-4" />
                Create Manually
              </Button>
            </Link>
          </div>
        </div>
      ) : viewMode === 'list' ? (
        /* List View */
        <div className="bg-card rounded-xl border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[300px]">Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Servings</TableHead>
                <TableHead>Time</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTemplates.map((template) => {
                const TypeIcon = typeIcons[template.type];
                return (
                  <TableRow key={template.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {template.image_url ? (
                          <img
                            src={template.image_url}
                            alt={template.name}
                            className="h-10 w-10 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center">
                            <TypeIcon className="h-5 w-5 text-muted-foreground/50" />
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="font-medium truncate">{template.name}</p>
                          {template.description && (
                            <p className="text-xs text-muted-foreground truncate max-w-[200px]">
                              {template.description}
                            </p>
                          )}
                        </div>
                        {template.source === 'ai_generated' && (
                          <Badge variant="secondary" className="gap-1 shrink-0">
                            <Sparkles className="h-3 w-3" />
                            AI
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5 capitalize">
                        <TypeIcon className="h-4 w-4 text-muted-foreground" />
                        {template.type}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-muted-foreground">
                        {template.category || '—'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span
                        className={cn(
                          'px-2 py-1 rounded-full text-xs font-medium',
                          statusColors[template.status]
                        )}
                      >
                        {template.status}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="flex items-center gap-1 text-muted-foreground">
                        <Users className="h-3.5 w-3.5" />
                        {template.servings}
                      </span>
                    </TableCell>
                    <TableCell>
                      {template.total_time_minutes ? (
                        <span className="flex items-center gap-1 text-muted-foreground">
                          <Timer className="h-3.5 w-3.5" />
                          {template.total_time_minutes}m
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-2">
                        <Link to={`/standardization/${template.id}`}>
                          <Button variant="outline" size="sm" className="gap-1.5">
                            <Eye className="h-3.5 w-3.5" />
                            View Details
                          </Button>
                        </Link>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem asChild>
                              <Link to={`/standardization/${template.id}`}>
                                <Edit2 className="h-4 w-4 mr-2" />
                                Edit Template
                              </Link>
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDuplicate(template)}>
                              <Copy className="h-4 w-4 mr-2" />
                              Duplicate
                            </DropdownMenuItem>
                            {template.status === 'draft' && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => handleApprove(template)}>
                                  <CheckCircle className="h-4 w-4 mr-2" />
                                  Approve
                                </DropdownMenuItem>
                              </>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => handleDelete(template)}
                              className="text-destructive"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      ) : (
        /* Card View */
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredTemplates.map((template) => {
            const TypeIcon = typeIcons[template.type];
            return (
              <div
                key={template.id}
                className="bg-card rounded-xl border overflow-hidden hover:shadow-lg transition-shadow"
              >
                {/* Image/Placeholder */}
                <div className="h-40 bg-gradient-to-br from-muted/50 to-muted relative">
                  {template.image_url ? (
                    <img
                      src={template.image_url}
                      alt={template.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <TypeIcon className="h-16 w-16 text-muted-foreground/30" />
                    </div>
                  )}
                  {/* Status Badge */}
                  <span
                    className={cn(
                      'absolute top-3 right-3 px-2 py-1 rounded-full text-xs font-medium',
                      statusColors[template.status]
                    )}
                  >
                    {template.status}
                  </span>
                  {/* Source Badge */}
                  {template.source === 'ai_generated' && (
                    <span className="absolute top-3 left-3 px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 flex items-center gap-1">
                      <Sparkles className="h-3 w-3" />
                      AI
                    </span>
                  )}
                </div>

                {/* Content */}
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold truncate">
                        {template.name}
                      </h3>
                      {template.category && (
                        <p className="text-xs text-muted-foreground">
                          {template.category}
                          {template.cuisine_type && ` • ${template.cuisine_type}`}
                        </p>
                      )}
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem asChild>
                          <Link to={`/standardization/${template.id}`}>
                            <Edit2 className="h-4 w-4 mr-2" />
                            Edit Template
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDuplicate(template)}>
                          <Copy className="h-4 w-4 mr-2" />
                          Duplicate
                        </DropdownMenuItem>
                        {template.status === 'draft' && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleApprove(template)}>
                              <CheckCircle className="h-4 w-4 mr-2" />
                              Approve
                            </DropdownMenuItem>
                          </>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => handleDelete(template)}
                          className="text-destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  {template.description && (
                    <p className="mt-2 text-sm text-muted-foreground line-clamp-2">
                      {template.description}
                    </p>
                  )}

                  {/* Meta Info */}
                  <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
                    {template.total_time_minutes && (
                      <span className="flex items-center gap-1">
                        <Timer className="h-3.5 w-3.5" />
                        {template.total_time_minutes} min
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Users className="h-3.5 w-3.5" />
                      {template.servings} servings
                    </span>
                    {template.estimated_cost_per_serving && (
                      <span className="flex items-center gap-1">
                        <DollarSign className="h-3.5 w-3.5" />
                        {template.estimated_cost_per_serving.toFixed(2)}/serving
                      </span>
                    )}
                  </div>

                  {/* Dietary Tags */}
                  {template.dietary_tags && template.dietary_tags.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1">
                      {template.dietary_tags.slice(0, 3).map((tag) => (
                        <span
                          key={tag}
                          className="px-2 py-0.5 text-xs rounded-full bg-muted text-muted-foreground"
                        >
                          {tag}
                        </span>
                      ))}
                      {template.dietary_tags.length > 3 && (
                        <span className="px-2 py-0.5 text-xs rounded-full bg-muted text-muted-foreground">
                          +{template.dietary_tags.length - 3}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="mt-4 pt-3 border-t">
                    <Link to={`/standardization/${template.id}`}>
                      <Button variant="secondary" className="w-full gap-2">
                        <Eye className="h-4 w-4" />
                        View Details
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

